import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Building2 } from 'lucide-react';

const companyDataSchema = z.object({
  company_name: z.string().min(2, 'Firmenname erforderlich').max(200, 'Maximal 200 Zeichen'),
  tax_id: z.string().max(50, 'Maximal 50 Zeichen').optional().or(z.literal('')),
  street_address: z.string().min(3, 'Straße erforderlich').max(200, 'Maximal 200 Zeichen'),
  postal_code: z.string().regex(/^\d{5}$/, 'Ungültige PLZ (5 Ziffern erforderlich)'),
  city: z.string().min(2, 'Stadt erforderlich').max(100, 'Maximal 100 Zeichen'),
  country: z.string().default('Deutschland'),
  contact_person: z.string().max(100, 'Maximal 100 Zeichen').optional().or(z.literal('')),
  phone: z.string()
    .regex(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/, 'Ungültige Telefonnummer')
    .optional()
    .or(z.literal('')),
});

type CompanyDataFormValues = z.infer<typeof companyDataSchema>;

interface CompanyDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const CompanyDataDialog = ({ open, onOpenChange, onSuccess }: CompanyDataDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<CompanyDataFormValues>({
    resolver: zodResolver(companyDataSchema),
    defaultValues: {
      company_name: '',
      tax_id: '',
      street_address: '',
      postal_code: '',
      city: '',
      country: 'Deutschland',
      contact_person: '',
      phone: '',
    },
  });

  const onSubmit = async (values: CompanyDataFormValues) => {
    if (!user) return;

    try {
      setSubmitting(true);
      
      const { error } = await supabase
        .from('company_profiles')
        .insert({
          user_id: user.id,
          company_name: values.company_name.trim(),
          tax_id: values.tax_id?.trim() || null,
          street_address: values.street_address.trim(),
          postal_code: values.postal_code.trim(),
          city: values.city.trim(),
          country: values.country,
          contact_person: values.contact_person?.trim() || null,
          phone: values.phone?.trim() || null,
        });

      if (error) {
        if (error.message.includes('unique')) {
          toast({
            title: 'Unternehmensdaten bereits vorhanden',
            description: 'Sie haben bereits Unternehmensdaten hinterlegt.',
            variant: 'destructive'
          });
        } else {
          throw error;
        }
        return;
      }

      toast({
        title: 'Unternehmensdaten gespeichert',
        description: 'Ihre Daten wurden erfolgreich gespeichert.'
      });
      
      form.reset();
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error('Error saving company data:', error);
      toast({
        title: 'Fehler beim Speichern',
        description: error.message || 'Die Unternehmensdaten konnten nicht gespeichert werden.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            Unternehmensdaten
          </DialogTitle>
          <DialogDescription>
            Bitte geben Sie Ihre Unternehmensdaten an, bevor Sie das Abonnement abschließen.
            Diese Informationen werden für die Rechnungsstellung benötigt.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="company_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Firmenname *</FormLabel>
                  <FormControl>
                    <Input placeholder="Beispiel GmbH" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tax_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Steuernummer / USt-IdNr.</FormLabel>
                  <FormControl>
                    <Input placeholder="DE123456789" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="street_address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Straße und Hausnummer *</FormLabel>
                    <FormControl>
                      <Input placeholder="Musterstraße 123" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="postal_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postleitzahl *</FormLabel>
                    <FormControl>
                      <Input placeholder="12345" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stadt *</FormLabel>
                    <FormControl>
                      <Input placeholder="Berlin" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Land *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="contact_person"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ansprechpartner</FormLabel>
                    <FormControl>
                      <Input placeholder="Max Mustermann" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefon</FormLabel>
                    <FormControl>
                      <Input placeholder="+49 123 456789" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Speichern...' : 'Speichern und fortfahren'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
